import {
  NcBadge,
  NcFormattedMessage,
  NcIconEllipsis,
  NcListBox,
  NcPanel,
} from "@noted/noted-components";
import { useId } from "react";

import { UnsignedRecord, useUnsignedRecordsQuery } from "~/api/records";
import { FeatureFlag, useFeatures } from "~/components/features.context";
import { useI18n } from "~/hooks/use-i18n";

import {
  LetterIcon,
  NoRecordsIcon,
  NoteIcon,
  RecordCourseIcon,
  SessionIcon,
  WhanauSessionIcon,
} from "./icons";

const PanelEmpty = () => {
  const { t } = useI18n("org");
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8">
      <NoRecordsIcon opacity="0.5" width="6rem" />
      <NcFormattedMessage variant="secondary">
        {t("org:dashboard.unsigned_records.empty")}
      </NcFormattedMessage>
    </div>
  );
};

function getRecordLink(
  record: UnsignedRecord,
  whanauFeatureLinkSegment: "whanau" | "whanaunga-awhina"
) {
  if (record.client) {
    return `/n/clients/${record.client.id}/records/consultation/${record.id}`;
  }
  if (record.group) {
    return `/n/groups/${record.group.id}/sessions/${record.id}`;
  }
  if (record.whanau) {
    return `/n/${whanauFeatureLinkSegment}/${record.whanau.id}/sessions/${record.id}`;
  }
  return "";
}

function getRecordIcon(recordType: string) {
  const iconProps = {
    width: "1.5rem",
  };
  switch (recordType) {
    case "NOTE":
      return <NoteIcon {...iconProps} />;
    case "LETTER":
      return <LetterIcon {...iconProps} />;
    case "SESSION":
      return <SessionIcon {...iconProps} />;
    case "WHANAU_SESSION":
      return <WhanauSessionIcon {...iconProps} />;
    default:
      return <RecordCourseIcon {...iconProps} />;
  }
}

const UnsignedRecordsPanel = () => {
  const { t } = useI18n("org");
  const panelId = useId();
  const { featureEnabled } = useFeatures();
  const whanauFeatureHrefSegment = featureEnabled(FeatureFlag.WhanaungaAwhina)
    ? "whanaunga-awhina"
    : "whanau";

  const { data: allUnsignedRecords, isLoading, error } = useUnsignedRecordsQuery();

  const unsignedRecords = allUnsignedRecords?.slice(0, 500);
  const moreUnsignedRecords = allUnsignedRecords?.length !== unsignedRecords?.length;

  return (
    <NcPanel>
      <NcPanel.Header>
        <NcPanel.Heading id={panelId} level={2} className="mr-auto">
          {t("org:dashboard.unsigned_records.heading")}
        </NcPanel.Heading>
        {unsignedRecords && (
          <NcBadge variant="danger">
            {unsignedRecords.length}
            {moreUnsignedRecords && "+"}
          </NcBadge>
        )}
      </NcPanel.Header>
      <NcPanel.Body
        className="p-0"
        isLoading={isLoading}
        error={
          error
            ? t("common:error_loading_refresh", {
                component: t("org:dashboard.unsigned_records.name"),
              })
            : undefined
        }
      >
        {unsignedRecords?.length ? (
          <NcListBox aria-labelledby={panelId} variant="borderless">
            {unsignedRecords.map((record: UnsignedRecord) => {
              const { id, client, name } = record;
              const isSession = record.type === "SESSION" || record.type === "WHANAU_SESSION";
              const displayName = isSession ? name : "";
              const fallbackName = t("org:dashboard.unsigned_records.untitled");
              const textValue = isSession
                ? displayName || fallbackName
                : `${client?.firstName} ${client?.lastName}`;

              return (
                <NcListBox.Item
                  key={`${record.type}_${id}`}
                  href={getRecordLink(record, whanauFeatureHrefSegment)}
                  textValue={textValue}
                >
                  <div className="flex items-center gap-2">
                    {getRecordIcon(record.type)}
                    <div data-testid="unsigned-record-name">
                      {client?.firstName ?? ""} {client?.lastName}
                      {isSession &&
                        (!name ? (
                          <span className="italic">
                            {t("org:dashboard.unsigned_records.untitled")}
                          </span>
                        ) : (
                          name
                        ))}
                    </div>
                  </div>
                </NcListBox.Item>
              );
            })}
            {moreUnsignedRecords && (
              <NcListBox.Item textValue={t("org:dashboard.unsigned_records.more_hidden")}>
                <NcIconEllipsis />
              </NcListBox.Item>
            )}
          </NcListBox>
        ) : (
          <PanelEmpty />
        )}
      </NcPanel.Body>
    </NcPanel>
  );
};

export default UnsignedRecordsPanel;
